import React, {
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRight,
  faCheck,
  faCopy,
  faCrown,
  faEnvelope,
  faEye,
  faEyeSlash,
  faKey,
  faLink,
  faPhone,
  faRightFromBracket,
  faSave,
  faShieldHalved,
  faTrash,
  faUpload,
  faUser,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";

import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from "firebase/storage";

import {
  signOut,
} from "firebase/auth";

import toast from "react-hot-toast";

import {
  auth,
} from "../firebase";

import {
  dashboardProfileApi,
} from "../api/dashboardProfileApi";

import type {
  DashboardProfile as DashboardProfileType,
} from "../types/dashboard-profile.types";

import "../screenstyles/DashboardProfile.css";

type SecurityLoading =
  | "password"
  | "reset-link"
  | "sessions"
  | null;

function formatDate(
  value:
    | string
    | null,
): string {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year:
        "numeric",

      month:
        "long",

      day:
        "2-digit",

      hour:
        "2-digit",

      minute:
        "2-digit",
    },
  ).format(date);
}

function getInitials(
  name: string,
): string {
  const cleaned =
    name.trim();

  if (!cleaned) {
    return "U";
  }

  const parts =
    cleaned
      .split(/\s+/)
      .filter(Boolean);

  if (
    parts.length === 1
  ) {
    return parts[0]
      .slice(0, 2)
      .toUpperCase();
  }

  return `${parts[0][0] ?? ""}${
    parts[1][0] ?? ""
  }`.toUpperCase();
}

function sanitizeFileName(
  fileName: string,
): string {
  return fileName
    .replace(
      /[^a-zA-Z0-9._-]/g,
      "-",
    )
    .replace(
      /-+/g,
      "-",
    );
}

const DashboardProfile:
React.FC = () => {
  const navigate =
    useNavigate();

  const storage =
    getStorage();

  const [
    profile,
    setProfile,
  ] =
    useState<
      DashboardProfileType |
      null
    >(null);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    saving,
    setSaving,
  ] =
    useState(false);

  const [
    photoLoading,
    setPhotoLoading,
  ] =
    useState(false);

  const [
    selectedPhoto,
    setSelectedPhoto,
  ] =
    useState<
      File |
      null
    >(null);

  const [
    photoPreview,
    setPhotoPreview,
  ] =
    useState<
      string |
      null
    >(null);

  const [
    securityLoading,
    setSecurityLoading,
  ] =
    useState<
      SecurityLoading
    >(null);

  const [
    newPassword,
    setNewPassword,
  ] =
    useState("");

  const [
    confirmPassword,
    setConfirmPassword,
  ] =
    useState("");

  const [
    showPassword,
    setShowPassword,
  ] =
    useState(false);

  const [
    passwordResetLink,
    setPasswordResetLink,
  ] =
    useState<
      string |
      null
    >(null);

  const [
    form,
    setForm,
  ] =
    useState({
      fullName:
        "",

      displayName:
        "",

      email:
        "",

      phoneNumber:
        "",
    });

  /**
   * =========================================================
   * Load Profile
   * =========================================================
   */

  const loadProfile =
    async () => {
      try {
        setLoading(
          true,
        );

        const result =
          await dashboardProfileApi
            .getMe();

        const currentProfile =
          result.profile;

        setProfile(
          currentProfile,
        );

        setForm({
          fullName:
            currentProfile
              .fullName ??
            "",

          displayName:
            currentProfile
              .displayName ??
            "",

          email:
            currentProfile
              .email ??
            "",

          phoneNumber:
            currentProfile
              .phoneNumber ??
            "",
        });
      } catch (
        error: any
      ) {
        console.error(
          "Failed to load dashboard profile:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تحميل الملف الشخصي",
        );
      } finally {
        setLoading(
          false,
        );
      }
    };

  useEffect(() => {
    loadProfile();
  }, []);

  /**
   * =========================================================
   * Selected photo preview
   * =========================================================
   */

  useEffect(() => {
    if (
      !selectedPhoto
    ) {
      setPhotoPreview(
        null,
      );

      return;
    }

    const objectUrl =
      URL.createObjectURL(
        selectedPhoto,
      );

    setPhotoPreview(
      objectUrl,
    );

    return () => {
      URL.revokeObjectURL(
        objectUrl,
      );
    };
  }, [
    selectedPhoto,
  ]);

  /**
   * =========================================================
   * Role name
   * =========================================================
   */

  const roleName =
    useMemo(
      () => {
        if (
          !profile?.role
        ) {
          return "بدون دور";
        }

        return (
          profile.role
            .nameAr ||
          profile.role.name
        );
      },
      [
        profile,
      ],
    );

  /**
   * =========================================================
   * Save basic profile
   * =========================================================
   */

  const handleSubmit =
    async (
      event:
        FormEvent,
    ) => {
      event.preventDefault();

      if (!profile) {
        return;
      }

      const fullName =
        form.fullName.trim();

      const email =
        form.email
          .trim()
          .toLowerCase();

      const displayName =
        form.displayName
          .trim();

      const phoneNumber =
        form.phoneNumber
          .trim();

      if (
        fullName.length <
        2
      ) {
        toast.error(
          "الاسم الكامل يجب أن يحتوي على حرفين على الأقل",
        );

        return;
      }

      if (!email) {
        toast.error(
          "أدخل البريد الإلكتروني",
        );

        return;
      }

      const emailPattern =
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (
        !emailPattern.test(
          email,
        )
      ) {
        toast.error(
          "صيغة البريد الإلكتروني غير صحيحة",
        );

        return;
      }

      const changed =
        fullName !==
          profile.fullName ||
        displayName !==
          (profile.displayName ??
            "") ||
        email !==
          profile.email ||
        phoneNumber !==
          (profile.phoneNumber ??
            "");

      if (!changed) {
        toast(
          "لا توجد تغييرات للحفظ",
        );

        return;
      }

      try {
        setSaving(
          true,
        );

        const result =
          await dashboardProfileApi
            .update({
              fullName,

              displayName:
                displayName ||
                null,

              email,

              phoneNumber:
                phoneNumber ||
                null,
            });

        setProfile(
          result.profile,
        );

        setForm({
          fullName:
            result.profile
              .fullName,

          displayName:
            result.profile
              .displayName ??
            "",

          email:
            result.profile
              .email,

          phoneNumber:
            result.profile
              .phoneNumber ??
            "",
        });

        toast.success(
          "تم تحديث الملف الشخصي بنجاح",
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to update dashboard profile:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تحديث الملف الشخصي",
        );
      } finally {
        setSaving(
          false,
        );
      }
    };

  /**
   * =========================================================
   * Upload profile photo
   * =========================================================
   */

  const handleUploadPhoto =
    async () => {
      if (
        !profile ||
        !selectedPhoto
      ) {
        return;
      }

      if (
        !selectedPhoto.type.startsWith(
          "image/",
        )
      ) {
        toast.error(
          "الملف المختار ليس صورة",
        );

        return;
      }

      const maxSize =
        5 *
        1024 *
        1024;

      if (
        selectedPhoto.size >
        maxSize
      ) {
        toast.error(
          "حجم الصورة يجب ألا يتجاوز 5MB",
        );

        return;
      }

      let uploadedPath:
        string |
        null = null;

      try {
        setPhotoLoading(
          true,
        );

        const safeName =
          sanitizeFileName(
            selectedPhoto.name,
          );

        uploadedPath =
          `dashboard-users/${
            profile.uid
          }/profile/${
            Date.now()
          }-${safeName}`;

        const photoRef =
          ref(
            storage,
            uploadedPath,
          );

        await uploadBytes(
          photoRef,
          selectedPhoto,
        );

        const photoUrl =
          await getDownloadURL(
            photoRef,
          );

        const oldPhotoPath =
          profile.photoPath;

        const result =
          await dashboardProfileApi
            .updatePhoto({
              photoUrl,

              photoPath:
                uploadedPath,
            });

        setProfile(
          result.profile,
        );

        setSelectedPhoto(
          null,
        );

        setPhotoPreview(
          null,
        );

        /**
         * نحذف الصورة القديمة
         * بعد نجاح تحديث الـAPI.
         */
        if (
          oldPhotoPath &&
          oldPhotoPath !==
            uploadedPath
        ) {
          try {
            await deleteObject(
              ref(
                storage,
                oldPhotoPath,
              ),
            );
          } catch (
            storageError
          ) {
            console.warn(
              "Could not delete old dashboard profile photo:",
              storageError,
            );
          }
        }

        toast.success(
          "تم تحديث صورة الحساب بنجاح",
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to upload dashboard profile photo:",
          error,
        );

        /**
         * إذا رُفعت الصورة إلى
         * Storage وفشل الـAPI،
         * نحذف الصورة الجديدة.
         */
        if (
          uploadedPath
        ) {
          try {
            await deleteObject(
              ref(
                storage,
                uploadedPath,
              ),
            );
          } catch {
            // لا نستبدل الخطأ الأصلي
          }
        }

        toast.error(
          error?.message ||
            "فشل تحديث صورة الحساب",
        );
      } finally {
        setPhotoLoading(
          false,
        );
      }
    };

  /**
   * =========================================================
   * Delete profile photo
   * =========================================================
   */

  const handleDeletePhoto =
    async () => {
      if (
        !profile?.photoUrl
      ) {
        return;
      }

      const confirmed =
        window.confirm(
          "هل تريد حذف صورة الحساب الحالية؟",
        );

      if (!confirmed) {
        return;
      }

      const oldPhotoPath =
        profile.photoPath;

      try {
        setPhotoLoading(
          true,
        );

        const result =
          await dashboardProfileApi
            .deletePhoto();

        setProfile(
          result.profile,
        );

        setSelectedPhoto(
          null,
        );

        setPhotoPreview(
          null,
        );

        if (
          oldPhotoPath
        ) {
          try {
            await deleteObject(
              ref(
                storage,
                oldPhotoPath,
              ),
            );
          } catch (
            storageError
          ) {
            console.warn(
              "Profile metadata removed, but Storage file could not be deleted:",
              storageError,
            );
          }
        }

        toast.success(
          "تم حذف صورة الحساب",
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to delete profile photo:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل حذف صورة الحساب",
        );
      } finally {
        setPhotoLoading(
          false,
        );
      }
    };

  /**
   * =========================================================
   * Change password
   * =========================================================
   */

  const handleChangePassword =
    async () => {
      if (
        newPassword.length <
        8
      ) {
        toast.error(
          "كلمة المرور يجب أن تكون 8 أحرف على الأقل",
        );

        return;
      }

      if (
        newPassword !==
        confirmPassword
      ) {
        toast.error(
          "كلمتا المرور غير متطابقتين",
        );

        return;
      }

      const confirmed =
        window.confirm(
          "بعد تغيير كلمة المرور سيتم إلغاء الجلسات الحالية. هل تريد المتابعة؟",
        );

      if (!confirmed) {
        return;
      }

      try {
        setSecurityLoading(
          "password",
        );

        const result =
          await dashboardProfileApi
            .updatePassword(
              newPassword,
            );

        if (
          result.updated
        ) {
          toast.success(
            result.sessionsRevoked
              ? "تم تغيير كلمة المرور وإلغاء الجلسات"
              : "تم تغيير كلمة المرور",
          );
        }

        setNewPassword("");
        setConfirmPassword("");
        setShowPassword(false);

        /**
         * بما أن الـBackend ألغى
         * كل Refresh Tokens،
         * ننهي جلسة المتصفح الحالية أيضًا.
         */
        await signOut(
          auth,
        );

        navigate(
          "/login",
          {
            replace: true,
          },
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to update password:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل تغيير كلمة المرور",
        );
      } finally {
        setSecurityLoading(
          null,
        );
      }
    };

  /**
   * =========================================================
   * Password reset link
   * =========================================================
   */

  const handleCreateResetLink =
    async () => {
      try {
        setSecurityLoading(
          "reset-link",
        );

        const result =
          await dashboardProfileApi
            .createPasswordResetLink();

        setPasswordResetLink(
          result.resetLink,
        );

        toast.success(
          "تم إنشاء رابط إعادة تعيين كلمة المرور",
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to generate reset link:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل إنشاء رابط إعادة التعيين",
        );
      } finally {
        setSecurityLoading(
          null,
        );
      }
    };

  const handleCopyResetLink =
    async () => {
      if (
        !passwordResetLink
      ) {
        return;
      }

      try {
        await navigator
          .clipboard
          .writeText(
            passwordResetLink,
          );

        toast.success(
          "تم نسخ الرابط",
        );
      } catch (
        error
      ) {
        console.error(
          "Failed to copy reset link:",
          error,
        );

        toast.error(
          "تعذر نسخ الرابط",
        );
      }
    };

  /**
   * =========================================================
   * Revoke sessions
   * =========================================================
   */

  const handleRevokeSessions =
    async () => {
      const confirmed =
        window.confirm(
          "سيتم تسجيل خروج حسابك من جميع الأجهزة بما فيها هذا الجهاز. هل أنت متأكد؟",
        );

      if (!confirmed) {
        return;
      }

      try {
        setSecurityLoading(
          "sessions",
        );

        const result =
          await dashboardProfileApi
            .revokeSessions();

        if (
          result.revoked
        ) {
          toast.success(
            "تم إلغاء جميع الجلسات",
          );
        }

        await signOut(
          auth,
        );

        navigate(
          "/login",
          {
            replace: true,
          },
        );
      } catch (
        error: any
      ) {
        console.error(
          "Failed to revoke sessions:",
          error,
        );

        toast.error(
          error?.message ||
            "فشل إلغاء الجلسات",
        );
      } finally {
        setSecurityLoading(
          null,
        );
      }
    };

  if (loading) {
    return (
      <div className="dashboard-profile-page">
        <div className="dashboard-profile-state">
          جاري تحميل الملف الشخصي...
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="dashboard-profile-page">
        <div className="dashboard-profile-state">
          تعذر تحميل الملف الشخصي.
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-profile-page">

      {/* =====================================================
          Header
      ====================================================== */}

      <div className="dashboard-profile-header">
        <div>
          <span className="dashboard-profile-eyebrow">
            حسابي
          </span>

          <h1>
            الملف الشخصي
          </h1>

          <p>
            إدارة بيانات حسابك،
            الصورة، معلومات الوصول
            وإعدادات الأمان.
          </p>
        </div>

        <button
          type="button"
          className="dashboard-profile-back"
          onClick={() =>
            navigate(-1)
          }
        >
          <FontAwesomeIcon
            icon={faArrowRight}
          />

          رجوع
        </button>
      </div>

      {/* =====================================================
          Profile hero
      ====================================================== */}

      <div className="dashboard-profile-summary-card">

        <div className="dashboard-profile-avatar-section">

          <div className="dashboard-profile-avatar">
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="معاينة الصورة"
              />
            ) : profile.photoUrl ? (
              <img
                src={profile.photoUrl}
                alt={
                  profile.fullName
                }
              />
            ) : (
              <span>
                {getInitials(
                  profile.fullName,
                )}
              </span>
            )}
          </div>

          <div className="dashboard-profile-avatar-actions">
            <label className="dashboard-profile-upload-btn">
              <FontAwesomeIcon
                icon={faUpload}
              />

              اختيار صورة

              <input
                type="file"
                accept="image/*"
                hidden
                onChange={event => {
                  const file =
                    event.target
                      .files?.[0];

                  if (file) {
                    setSelectedPhoto(
                      file,
                    );
                  }

                  event.target.value =
                    "";
                }}
              />
            </label>

            {selectedPhoto && (
              <button
                type="button"
                className="dashboard-profile-photo-save-btn"
                disabled={
                  photoLoading
                }
                onClick={
                  handleUploadPhoto
                }
              >
                <FontAwesomeIcon
                  icon={faSave}
                />

                {photoLoading
                  ? "جاري الرفع..."
                  : "حفظ الصورة"}
              </button>
            )}

            {selectedPhoto && (
              <button
                type="button"
                className="dashboard-profile-photo-cancel-btn"
                onClick={() =>
                  setSelectedPhoto(
                    null,
                  )
                }
              >
                إلغاء
              </button>
            )}

            {profile.photoUrl &&
              !selectedPhoto && (
                <button
                  type="button"
                  className="dashboard-profile-photo-delete-btn"
                  disabled={
                    photoLoading
                  }
                  onClick={
                    handleDeletePhoto
                  }
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                  />

                  حذف الصورة
                </button>
              )}
          </div>

        </div>

        <div className="dashboard-profile-summary-info">
          <h2>
            {profile.fullName}
          </h2>

          <span>
            {profile.email}
          </span>

          <div className="dashboard-profile-summary-badges">
            <span className="dashboard-profile-role-badge">
              <FontAwesomeIcon
                icon={
                  faShieldHalved
                }
              />

              {roleName}
            </span>

            <span
              className={
                profile.isActive &&
                !profile.authDisabled
                  ? "dashboard-profile-status-badge active"
                  : "dashboard-profile-status-badge inactive"
              }
            >
              <FontAwesomeIcon
                icon={
                  profile.isActive &&
                  !profile.authDisabled
                    ? faUserCheck
                    : faUser
                }
              />

              {profile.isActive &&
              !profile.authDisabled
                ? "الحساب نشط"
                : "الحساب معطل"}
            </span>

            {profile.isSuperAdmin && (
              <span className="dashboard-profile-super-badge">
                <FontAwesomeIcon
                  icon={faCrown}
                />

                Super Admin
              </span>
            )}
          </div>
        </div>

      </div>

      {/* =====================================================
          Basic data
      ====================================================== */}

      <form
        className="dashboard-profile-card"
        onSubmit={
          handleSubmit
        }
      >
        <div className="dashboard-profile-section-title">
          <FontAwesomeIcon
            icon={faUser}
          />

          <div>
            <h2>
              البيانات الشخصية
            </h2>

            <p>
              يمكنك تعديل معلومات
              حسابك الأساسية.
            </p>
          </div>
        </div>

        <div className="dashboard-profile-form-grid">

          <div className="dashboard-profile-field">
            <label>
              الاسم الكامل
            </label>

            <input
              type="text"
              value={
                form.fullName
              }
              onChange={event =>
                setForm(
                  previous => ({
                    ...previous,

                    fullName:
                      event.target
                        .value,
                  }),
                )
              }
            />
          </div>

          <div className="dashboard-profile-field">
            <label>
              الاسم الظاهر
            </label>

            <input
              type="text"
              value={
                form.displayName
              }
              onChange={event =>
                setForm(
                  previous => ({
                    ...previous,

                    displayName:
                      event.target
                        .value,
                  }),
                )
              }
            />
          </div>

          <div className="dashboard-profile-field">
            <label>
              البريد الإلكتروني
            </label>

            <div className="dashboard-profile-input-icon">
              <FontAwesomeIcon
                icon={faEnvelope}
              />

              <input
                type="email"
                value={
                  form.email
                }
                onChange={event =>
                  setForm(
                    previous => ({
                      ...previous,

                      email:
                        event.target
                          .value,
                    }),
                  )
                }
                dir="ltr"
              />
            </div>
          </div>

          <div className="dashboard-profile-field">
            <label>
              رقم الهاتف
            </label>

            <div className="dashboard-profile-input-icon">
              <FontAwesomeIcon
                icon={faPhone}
              />

              <input
                type="text"
                value={
                  form.phoneNumber
                }
                onChange={event =>
                  setForm(
                    previous => ({
                      ...previous,

                      phoneNumber:
                        event.target
                          .value,
                    }),
                  )
                }
                dir="ltr"
              />
            </div>
          </div>

        </div>

        <div className="dashboard-profile-form-actions">
          <button
            type="submit"
            className="dashboard-profile-primary-btn"
            disabled={
              saving
            }
          >
            <FontAwesomeIcon
              icon={faSave}
            />

            {saving
              ? "جاري الحفظ..."
              : "حفظ التغييرات"}
          </button>
        </div>
      </form>

      {/* =====================================================
          Access
      ====================================================== */}

      <div className="dashboard-profile-card">
        <div className="dashboard-profile-section-title">
          <FontAwesomeIcon
            icon={
              faShieldHalved
            }
          />

          <div>
            <h2>
              الوصول والصلاحيات
            </h2>

            <p>
              معلومات للعرض فقط.
              تعديلها يتم من إدارة
              مستخدمي الداش بورد.
            </p>
          </div>
        </div>

        <div className="dashboard-profile-access-grid">

          <div className="dashboard-profile-info-card">
            <span>
              الدور
            </span>

            <strong>
              {roleName}
            </strong>

            {profile.roleId && (
              <small>
                {profile.roleId}
              </small>
            )}
          </div>

          <div className="dashboard-profile-info-card">
            <span>
              الصلاحيات المباشرة
            </span>

            <strong>
              {
                profile
                  .permissions
                  .directPermissionIds
                  .length
              }
            </strong>
          </div>

          <div className="dashboard-profile-info-card">
            <span>
              صلاحيات الدور
            </span>

            <strong>
              {
                profile
                  .permissions
                  .rolePermissionIds
                  .length
              }
            </strong>
          </div>

          <div className="dashboard-profile-info-card">
            <span>
              إجمالي الصلاحيات
            </span>

            <strong>
              {
                profile
                  .permissions
                  .effectivePermissionIds
                  .length
              }
            </strong>
          </div>

        </div>

        <div className="dashboard-profile-permissions-list">
          {profile
            .permissions
            .effectivePermissionIds
            .length === 0 ? (
              <span className="dashboard-profile-empty">
                لا توجد صلاحيات فعالة
              </span>
            ) : (
              profile
                .permissions
                .effectivePermissionIds
                .map(
                  permission => (
                    <span
                      key={
                        permission
                      }
                    >
                      <FontAwesomeIcon
                        icon={
                          faCheck
                        }
                      />

                      {
                        permission
                      }
                    </span>
                  ),
                )
            )}
        </div>
      </div>

      {/* =====================================================
          Account information
      ====================================================== */}

      <div className="dashboard-profile-card">
        <div className="dashboard-profile-section-title">
          <FontAwesomeIcon
            icon={
              faUserCheck
            }
          />

          <div>
            <h2>
              معلومات الحساب
            </h2>

            <p>
              معلومات Firebase
              وسجل الحساب.
            </p>
          </div>
        </div>

        <div className="dashboard-profile-meta-grid">

          <div>
            <span>
              UID
            </span>

            <strong dir="ltr">
              {profile.uid}
            </strong>
          </div>

          <div>
            <span>
              حالة البريد
            </span>

            <strong>
              {profile.auth
                .emailVerified
                ? "تم التحقق"
                : "غير مؤكد"}
            </strong>
          </div>

          <div>
            <span>
              تاريخ إنشاء الحساب
            </span>

            <strong>
              {formatDate(
                profile.createdAt ||
                  profile.auth
                    .creationTime,
              )}
            </strong>
          </div>

          <div>
            <span>
              آخر تسجيل دخول
            </span>

            <strong>
              {formatDate(
                profile.lastLoginAt ||
                  profile.auth
                    .lastSignInTime,
              )}
            </strong>
          </div>

          <div>
            <span>
              آخر تحديث
            </span>

            <strong>
              {formatDate(
                profile.updatedAt,
              )}
            </strong>
          </div>

          <div>
            <span>
              Firebase Auth
            </span>

            <strong>
              {profile.authDisabled
                ? "معطل"
                : "نشط"}
            </strong>
          </div>

        </div>
      </div>

      {/* =====================================================
          Security
      ====================================================== */}

      <div className="dashboard-profile-card">
        <div className="dashboard-profile-section-title">
          <FontAwesomeIcon
            icon={faKey}
          />

          <div>
            <h2>
              الأمان والجلسات
            </h2>

            <p>
              تغيير كلمة المرور
              وإدارة الجلسات الحالية.
            </p>
          </div>
        </div>

        <div className="dashboard-profile-security-grid">

          {/* Password */}

          <div className="dashboard-profile-security-card">
            <h3>
              تغيير كلمة المرور
            </h3>

            <p>
              تعيين كلمة مرور جديدة
              لحسابك.
            </p>

            <div className="dashboard-profile-field">
              <label>
                كلمة المرور الجديدة
              </label>

              <div className="dashboard-profile-password-input">
                <input
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  value={
                    newPassword
                  }
                  onChange={event =>
                    setNewPassword(
                      event.target
                        .value,
                    )
                  }
                  placeholder="8 أحرف على الأقل"
                  dir="ltr"
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowPassword(
                      previous =>
                        !previous,
                    )
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      showPassword
                        ? faEyeSlash
                        : faEye
                    }
                  />
                </button>
              </div>
            </div>

            <div className="dashboard-profile-field">
              <label>
                تأكيد كلمة المرور
              </label>

              <input
                type={
                  showPassword
                    ? "text"
                    : "password"
                }
                value={
                  confirmPassword
                }
                onChange={event =>
                  setConfirmPassword(
                    event.target
                      .value,
                  )
                }
                dir="ltr"
              />
            </div>

            <button
              type="button"
              className="dashboard-profile-primary-btn"
              disabled={
                securityLoading !==
                null
              }
              onClick={
                handleChangePassword
              }
            >
              <FontAwesomeIcon
                icon={faKey}
              />

              {securityLoading ===
              "password"
                ? "جاري التغيير..."
                : "تغيير كلمة المرور"}
            </button>
          </div>

          {/* Reset Link */}

          <div className="dashboard-profile-security-card">
            <h3>
              رابط إعادة تعيين كلمة المرور
            </h3>

            <p>
              إنشاء رابط Firebase
              لإعادة تعيين كلمة مرور
              حسابك.
            </p>

            <button
              type="button"
              className="dashboard-profile-secondary-btn"
              disabled={
                securityLoading !==
                null
              }
              onClick={
                handleCreateResetLink
              }
            >
              <FontAwesomeIcon
                icon={faLink}
              />

              {securityLoading ===
              "reset-link"
                ? "جاري الإنشاء..."
                : "إنشاء رابط الاستعادة"}
            </button>

            {passwordResetLink && (
              <div className="dashboard-profile-reset-box">
                <label>
                  رابط إعادة التعيين
                </label>

                <div>
                  <input
                    value={
                      passwordResetLink
                    }
                    readOnly
                    dir="ltr"
                  />

                  <button
                    type="button"
                    onClick={
                      handleCopyResetLink
                    }
                  >
                    <FontAwesomeIcon
                      icon={
                        faCopy
                      }
                    />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Sessions */}

          <div className="dashboard-profile-security-card dashboard-profile-danger-card">
            <h3>
              تسجيل الخروج من جميع الأجهزة
            </h3>

            <p>
              سيتم إلغاء جميع
              Refresh Tokens الخاصة
              بحسابك وتسجيل خروجك
              من الجلسة الحالية أيضًا.
            </p>

            <button
              type="button"
              className="dashboard-profile-danger-btn"
              disabled={
                securityLoading !==
                null
              }
              onClick={
                handleRevokeSessions
              }
            >
              <FontAwesomeIcon
                icon={
                  faRightFromBracket
                }
              />

              {securityLoading ===
              "sessions"
                ? "جاري تسجيل الخروج..."
                : "تسجيل خروج من جميع الأجهزة"}
            </button>
          </div>

        </div>
      </div>

    </div>
  );
};

export default DashboardProfile;